import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/shared/components/ui/Dialog';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { AccountGroup, useCreateAccountGroup, useUpdateAccountGroup } from '../hooks/useAccountGroupsApi';
import { Loader2 } from 'lucide-react';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editGroup?: AccountGroup | null;
}

type FormData = {
  name: string;
  type: string;
  category: string;
  description: string;
};

const ACCOUNT_TYPES = ['Capital', 'Expense', 'Income', 'Asset', 'Liability'];

export function AccountGroupFormModal({ open, onOpenChange, editGroup }: Props) {
  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormData>({
    defaultValues: {
      name: '',
      type: 'Expense',
      category: '',
      description: '',
    }
  });

  const createGroup = useCreateAccountGroup();
  const updateGroup = useUpdateAccountGroup();
  
  const isSubmitting = createGroup.isPending || updateGroup.isPending;

  useEffect(() => {
    if (open) {
      if (editGroup) {
        reset({
          name: editGroup.name,
          type: editGroup.type,
          category: editGroup.category,
          description: editGroup.description || '',
        });
      } else {
        reset({ name: '', type: 'Expense', category: '', description: '' });
      }
    }
  }, [open, editGroup, reset]);

  const onSubmit = (data: FormData) => {
    if (editGroup) {
      updateGroup.mutate({ id: editGroup.id, data }, {
        onSuccess: () => onOpenChange(false)
      });
    } else {
      createGroup.mutate(data, {
        onSuccess: () => onOpenChange(false)
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{editGroup ? 'Edit Account Group' : 'Create Account Group'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 py-4">
          <div className="space-y-2">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">Group Name <span className="text-red-500">*</span></label>
            <Input 
              id="name" 
              placeholder="e.g. Cost Of Goods Sold" 
              {...register('name', { required: 'Name is required' })} 
            />
            {errors.name && <p className="text-xs text-red-500">{errors.name.message}</p>}
          </div>

          <div className="space-y-2">
            <label htmlFor="type" className="block text-sm font-medium text-gray-700">Account Type <span className="text-red-500">*</span></label>
            <select 
              id="type"
              className="flex h-10 w-full rounded-md border border-gray-300 bg-transparent px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50"
              {...register('type', { required: 'Type is required' })}
            >
              {ACCOUNT_TYPES.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            {errors.type && <p className="text-xs text-red-500">{errors.type.message}</p>}
          </div>

          <div className="space-y-2">
            <label htmlFor="category" className="block text-sm font-medium text-gray-700">Category <span className="text-red-500">*</span></label>
            <Input 
              id="category" 
              placeholder="e.g. Equities, Direct Expenses" 
              {...register('category', { required: 'Category is required' })} 
            />
            {errors.category && <p className="text-xs text-red-500">{errors.category.message}</p>}
          </div>

          <div className="space-y-2">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">Description</label>
            <textarea 
              id="description" 
              className="flex w-full rounded-md border border-gray-300 bg-transparent px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50 min-h-[80px] resize-y"
              placeholder="Optional description" 
              {...register('description')} 
            />
          </div>

          <DialogFooter className="pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editGroup ? 'Save Changes' : 'Create Group'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
